/*
 * Let's Dish checkout delivery preference.
 *
 * Before the shopper leaves for checkout, tell Shopify which delivery method
 * the site already knows about, so checkout opens on "Pick up" (with the
 * chosen store) for pickup carts and on "Ship" for shipping carts.
 *
 * Uses the Storefront API cartBuyerIdentityUpdate mutation on the shopper's
 * own Ajax cart (cart token from /cart.js maps to gid://shopify/Cart/<token>).
 * Fails open: any error, or no answer within TIMEOUT_MS, goes to checkout anyway.
 */
(() => {
  if (window.LetsDishCheckoutPrefs) return;

  const config = window.LetsDishCheckoutPrefsConfig || {};
  const API_VERSION = config.apiVersion || '2025-10';
  const TOKEN = config.storefrontToken || '';
  const TIMEOUT_MS = Number(config.timeoutMs) || 1500;
  const CACHE_KEY = 'ld-checkout-pref-sig';

  // Store handle / Shopify location name (handleized) -> Shopify location ID.
  // pickupHandle for local pickup expects the numeric location ID.
  const LOCATION_IDS = Object.assign({
    'apple-valley': '28489547876',
    'let-s-dish-av': '28489547876',
    'lets-dish-av': '28489547876',
    'eden-prairie': '28489711716',
    'let-s-dish-ep': '28489711716',
    'lets-dish-ep': '28489711716',
    'maple-grove': '28489744484',
    'let-s-dish-mg': '28489744484',
    'lets-dish-mg': '28489744484',
    'prior-lake': '60926361635',
    'let-s-dish-prior-lake': '60926361635',
    'lets-dish-prior-lake': '60926361635',
    woodbury: '28230680676',
    'let-s-dish-wb': '28230680676',
    'lets-dish-wb': '28230680676'
  }, config.locationIds || {});

  const MUTATION = `mutation LdCheckoutPref($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
  cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
    cart { id }
    userErrors { code message }
  }
}`;

  const READ_QUERY = `query LdCheckoutPrefCart($cartId: ID!) {
  cart(id: $cartId) { buyerIdentity { email countryCode } }
}`;

  async function storefront(query, variables) {
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (TOKEN) headers['X-Shopify-Storefront-Access-Token'] = TOKEN;
    const response = await fetch(`/api/${API_VERSION}/graphql.json`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, variables })
    });
    if (!response.ok) throw new Error(`storefront ${response.status}`);
    const json = await response.json();
    if (json && json.errors && json.errors.length) throw new Error('storefront errors');
    return json && json.data;
  }

  function handleize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function withTimeout(promise, ms) {
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve('timeout'), ms);
      promise.then(
        (value) => { clearTimeout(timer); resolve(value); },
        () => { clearTimeout(timer); resolve('error'); }
      );
    });
  }

  function readCache() {
    try { return sessionStorage.getItem(CACHE_KEY); } catch (error) { return null; }
  }

  function writeCache(value) {
    try { sessionStorage.setItem(CACHE_KEY, value); } catch (error) { /* ignore */ }
  }

  function resolveLocationId(attrs) {
    let stored = '';
    try { stored = localStorage.getItem('selected-store') || ''; } catch (error) { /* ignore */ }
    const candidates = [attrs.pickup_location, attrs.pickup_location_name, stored];
    for (const candidate of candidates) {
      const id = LOCATION_IDS[handleize(candidate)];
      if (id) return id;
    }
    return '';
  }

  function preferenceFor(cart) {
    const attrs = (cart && cart.attributes) || {};
    let mode = attrs.fulfillment_mode || '';
    if (!mode) {
      try { mode = localStorage.getItem('fulfillment-mode') || ''; } catch (error) { /* ignore */ }
    }
    if (mode === 'delivery') {
      return { deliveryMethod: ['SHIPPING'], pickupHandle: [] };
    }
    if (mode === 'pickup') {
      const locationId = resolveLocationId(attrs);
      return locationId
        ? { deliveryMethod: ['PICK_UP'], pickupHandle: [locationId] }
        : { deliveryMethod: ['PICK_UP'] };
    }
    return null;
  }

  async function getCart() {
    const response = await fetch(`${cartRoot()}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  async function sync() {
    const cart = await getCart();
    if (!cart || !cart.token || !(cart.item_count > 0)) return 'empty';
    const delivery = preferenceFor(cart);
    if (!delivery) return 'no-mode';

    const signature = `${cart.token}|${JSON.stringify(delivery)}`;
    if (readCache() === signature) return 'cached';

    const cartId = `gid://shopify/Cart/${cart.token}`;

    // cartBuyerIdentityUpdate replaces omitted fields, so carry over email + country.
    const current = await storefront(READ_QUERY, { cartId });
    const existing = (current && current.cart && current.cart.buyerIdentity) || {};
    const buyerIdentity = { preferences: { delivery } };
    if (existing.email) buyerIdentity.email = existing.email;
    if (existing.countryCode) buyerIdentity.countryCode = existing.countryCode;

    const data = await storefront(MUTATION, { cartId, buyerIdentity });
    const result = data && data.cartBuyerIdentityUpdate;
    if (!result || !result.cart || (result.userErrors && result.userErrors.length)) {
      throw new Error('storefront userErrors');
    }
    writeCache(signature);
    return 'updated';
  }

  let inFlight = null;
  function syncOnce() {
    if (!inFlight) {
      inFlight = sync().finally(() => { inFlight = null; });
    }
    return inFlight;
  }

  // Checkout buttons are submit buttons named "checkout" (cart page and cart drawer).
  let lastCheckoutButton = null;
  document.addEventListener('click', (event) => {
    const button = event.target && event.target.closest && event.target.closest('button[name="checkout"], input[name="checkout"]');
    if (button) lastCheckoutButton = button;
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.ldCheckoutPrefDone === '1') return;
    const submitter = event.submitter || lastCheckoutButton;
    if (!submitter || submitter.name !== 'checkout') return;
    if (submitter.form && submitter.form !== form) return;

    event.preventDefault();
    form.dataset.ldCheckoutPrefDone = '1';
    submitter.setAttribute('aria-busy', 'true');

    withTimeout(syncOnce(), TIMEOUT_MS).then(() => {
      let marker = form.querySelector('input[type="hidden"][name="checkout"][data-ld-checkout-pref]');
      if (!marker) {
        marker = document.createElement('input');
        marker.type = 'hidden';
        marker.name = 'checkout';
        marker.value = submitter.value || '';
        marker.setAttribute('data-ld-checkout-pref', '');
        form.appendChild(marker);
      }
      // Native submit skips submit listeners, so this cannot loop.
      HTMLFormElement.prototype.submit.call(form);
      // If the page comes back from bfcache, allow the next click to sync again.
      setTimeout(() => {
        delete form.dataset.ldCheckoutPrefDone;
        submitter.removeAttribute('aria-busy');
      }, 4000);
    });
  }, true);

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('form[data-ld-checkout-pref-done]').forEach((form) => {
      delete form.dataset.ldCheckoutPrefDone;
    });
  });

  // Background sync so express buttons and direct /checkout visits also get the preference.
  function backgroundSync() {
    syncOnce().catch(() => { /* fail open */ });
  }
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
  if (document.readyState === 'complete') {
    idle(backgroundSync);
  } else {
    window.addEventListener('load', () => idle(backgroundSync), { once: true });
  }
  document.addEventListener('storechanged', () => setTimeout(backgroundSync, 2500));

  window.LetsDishCheckoutPrefs = { sync: syncOnce, preferenceFor, resolveLocationId };
})();
